import { useState, useEffect, useCallback } from 'react';
import { Users, Download, Filter, RefreshCw, LogOut, ChevronLeft, ChevronRight, Activity, Trash2, Handshake, Building2, Stethoscope, Megaphone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLLAB_TYPE_LABELS = {
  brand_business: { label: 'Brand / Business', Icon: Building2 },
  doctor_physician: { label: 'Doctor / Physician', Icon: Stethoscope },
  influencer_kol: { label: 'Influencer / KOL', Icon: Megaphone },
};

export const AdminDashboard = () => {
  const [auth, setAuth] = useState(() => {
    try {
      const saved = sessionStorage.getItem('vc_admin');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterInterest, setFilterInterest] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collabEntries, setCollabEntries] = useState([]);
  const [collabTotal, setCollabTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('waitlist');
  const LIMIT = 20;

  // Persist auth to sessionStorage
  useEffect(() => {
    if (auth) {
      sessionStorage.setItem('vc_admin', JSON.stringify(auth));
    } else {
      sessionStorage.removeItem('vc_admin');
    }
  }, [auth]);

  const authHeaders = useCallback(() => {
    if (!auth) return {};
    return { Authorization: 'Basic ' + btoa(auth.user + ':' + auth.pass) };
  }, [auth]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/stats`, { headers: authHeaders() });
      if (res.status === 401) { setError('Invalid credentials'); setAuth(null); return; }
      const data = await res.json();
      setStats(data);
    } catch { setError('Failed to fetch stats'); }
  }, [authHeaders]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/waitlist?skip=${page * LIMIT}&limit=${LIMIT}`;
      if (filterInterest) url += `&interest=${encodeURIComponent(filterInterest)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 401) { setError('Invalid credentials'); setAuth(null); return; }
      const data = await res.json();
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch { setError('Failed to fetch entries'); }
    setLoading(false);
  }, [page, filterInterest, authHeaders]);

  const fetchCollaborations = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/collaborations`, { headers: authHeaders() });
      if (res.status === 401) return;
      const data = await res.json();
      setCollabEntries(data.entries || []);
      setCollabTotal(data.total || 0);
    } catch { /* silently fail */ }
  }, [authHeaders]);

  useEffect(() => {
    if (auth) { fetchStats(); fetchEntries(); fetchCollaborations(); }
  }, [auth, fetchStats, fetchEntries, fetchCollaborations]);

  const handleExport = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/export`, { headers: authHeaders() });
      const data = await res.json();
      const csv = convertToCSV(data);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verse_waitlist_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Export failed'); }
  };

  const handleResetDB = async () => {
    if (!window.confirm('Are you sure you want to delete ALL waitlist entries? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reset-db`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Deleted ${data.deleted_entries} entries. Database reset.`);
        fetchStats();
        fetchEntries();
      } else {
        setError('Reset failed');
      }
    } catch { setError('Reset failed'); }
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    const headers = ['full_name', 'email', 'phone', 'interest', 'wished_treatments', 'position', 'timestamp', 'source'];
    const rows = data.map((row) => headers.map((h) => {
      const val = row[h];
      if (Array.isArray(val)) return `"${val.join(', ')}"`;
      return `"${(val || '').toString().replace(/"/g, '""')}"`;
    }).join(','));
    return [headers.join(','), ...rows].join('\n');
  };

  // Login form
  if (!auth) {
    return <LoginForm onLogin={(u, p) => { setAuth({ user: u, pass: p }); setError(''); }} error={error} />;
  }

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ backgroundColor: '#071525', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="border-b px-6 py-4 flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5" style={{ color: '#d6a84a' }} />
          <h1 className="text-lg font-semibold" style={{ color: '#f4f0e8' }}>VERSE Admin</h1>
        </div>
        <button
          onClick={() => setAuth(null)}
          className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors duration-200"
          style={{ color: '#9ca3af', backgroundColor: 'transparent' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Signups" value={stats.total} />
            {stats.by_interest?.slice(0, 3).map((item) => (
              <StatCard key={item.interest} label={item.interest} value={item.count} />
            ))}
          </div>
        )}

        {/* Treatment Interest Breakdown */}
        {stats && stats.by_treatment && stats.by_treatment.length > 0 && (
          <div
            data-testid="admin-treatment-stats"
            className="rounded-xl border p-6 mb-8"
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center gap-2 mb-5">
              <Activity className="w-4 h-4" style={{ color: '#d6a84a' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#f4f0e8', fontFamily: 'Inter, sans-serif' }}>
                Treatment Interest
              </h2>
              <span className="text-xs ml-auto" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>
                {stats.by_treatment.reduce((s, t) => s + t.count, 0)} selections
              </span>
            </div>
            <div className="space-y-3">
              {stats.by_treatment.map((item) => {
                const maxCount = stats.by_treatment[0]?.count || 1;
                const pct = Math.round((item.count / maxCount) * 100);
                return (
                  <div key={item.treatment} className="flex items-center gap-3">
                    <span className="text-sm w-48 truncate flex-shrink-0" style={{ color: '#c3bfb4', fontFamily: 'Inter, sans-serif' }}>
                      {item.treatment}
                    </span>
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: '#d6a84a', transition: 'width 600ms ease' }}
                      />
                    </div>
                    <span className="text-xs font-medium w-8 text-right flex-shrink-0" style={{ color: '#d6a84a', fontFamily: 'Inter, sans-serif' }}>
                      {item.count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 mb-6 p-1 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            data-testid="admin-tab-waitlist"
            onClick={() => setActiveTab('waitlist')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
            style={{
              backgroundColor: activeTab === 'waitlist' ? 'rgba(214,168,74,0.15)' : 'transparent',
              color: activeTab === 'waitlist' ? '#d6a84a' : '#9ca3af',
              border: activeTab === 'waitlist' ? '1px solid rgba(214,168,74,0.3)' : '1px solid transparent',
            }}
          >
            <Users className="w-3.5 h-3.5" /> Waitlist
            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(214,168,74,0.1)', color: '#d6a84a' }}>{total}</span>
          </button>
          <button
            data-testid="admin-tab-partnerships"
            onClick={() => setActiveTab('partnerships')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
            style={{
              backgroundColor: activeTab === 'partnerships' ? 'rgba(214,168,74,0.15)' : 'transparent',
              color: activeTab === 'partnerships' ? '#d6a84a' : '#9ca3af',
              border: activeTab === 'partnerships' ? '1px solid rgba(214,168,74,0.3)' : '1px solid transparent',
            }}
          >
            <Handshake className="w-3.5 h-3.5" /> Partnerships
            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(214,168,74,0.1)', color: '#d6a84a' }}>{collabTotal}</span>
          </button>
        </div>

        {/* ─── Waitlist Tab ─── */}
        {activeTab === 'waitlist' && (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <select
                data-testid="admin-filter-interest"
                value={filterInterest}
                onChange={(e) => { setFilterInterest(e.target.value); setPage(0); }}
                className="h-9 px-3 rounded-lg border text-sm outline-none"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  borderColor: 'rgba(255,255,255,0.1)',
                  color: '#f4f0e8',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <option value="" style={{ backgroundColor: '#1a2332', color: '#f4f0e8' }}>All Interests</option>
                <option value="Longevity & BioAge" style={{ backgroundColor: '#1a2332', color: '#f4f0e8' }}>Longevity & BioAge</option>
                <option value="Aesthetic Treatments" style={{ backgroundColor: '#1a2332', color: '#f4f0e8' }}>Aesthetic Treatments</option>
                <option value="Regenerative Therapies" style={{ backgroundColor: '#1a2332', color: '#f4f0e8' }}>Regenerative Therapies</option>
                <option value="Not sure — advise me" style={{ backgroundColor: '#1a2332', color: '#f4f0e8' }}>Not sure — advise me</option>
              </select>

              <button
                onClick={() => { fetchStats(); fetchEntries(); }}
                className="h-9 px-3 rounded-lg border flex items-center gap-2 text-sm transition-colors duration-200"
                style={{ borderColor: 'rgba(255,255,255,0.1)', color: '#9ca3af', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>

              <button
                data-testid="admin-export-btn"
                onClick={handleExport}
                className="h-9 px-3 rounded-lg flex items-center gap-2 text-sm transition-colors duration-200"
                style={{ backgroundColor: 'rgba(214,168,74,0.15)', color: '#d6a84a', border: '1px solid rgba(214,168,74,0.3)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.15)'}
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>

              <button
                data-testid="admin-reset-db-btn"
                onClick={handleResetDB}
                className="h-9 px-3 rounded-lg flex items-center gap-2 text-sm transition-colors duration-200"
                style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'}
              >
                <Trash2 className="w-3.5 h-3.5" /> Reset DB
              </button>

              <span className="ml-auto text-xs" style={{ color: '#9ca3af' }}>
                {total} {total === 1 ? 'entry' : 'entries'}
              </span>
            </div>

            {/* Waitlist Table */}
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      {['#', 'Name', 'Email', 'Phone', 'Interest', 'Treatments', 'Date'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#9ca3af' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: '#9ca3af' }}>Loading...</td></tr>
                    ) : entries.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: '#9ca3af' }}>No entries found</td></tr>
                    ) : entries.map((entry, i) => (
                      <tr key={entry.id || i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <td className="px-4 py-3 text-xs" style={{ color: '#d6a84a' }}>#{entry.position || '-'}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: '#f4f0e8' }}>{entry.full_name}</td>
                        <td className="px-4 py-3" style={{ color: '#c3bfb4' }}>{entry.email}</td>
                        <td className="px-4 py-3" style={{ color: '#c3bfb4' }}>{entry.phone}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(214,168,74,0.1)', color: '#d6a84a' }}>
                            {entry.interest}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {entry.wished_treatments && entry.wished_treatments.length > 0 ? (
                              entry.wished_treatments.map((t, idx) => (
                                <span key={idx} className="px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: 'rgba(214,168,74,0.06)', color: '#c3bfb4', border: '1px solid rgba(255,255,255,0.08)' }}>
                                  {t}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs" style={{ color: '#6b7280' }}>-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#9ca3af' }}>
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg disabled:opacity-30"
                    style={{ color: '#9ca3af' }}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Previous
                  </button>
                  <span className="text-xs" style={{ color: '#9ca3af' }}>Page {page + 1} of {totalPages}</span>
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg disabled:opacity-30"
                    style={{ color: '#9ca3af' }}
                  >
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ─── Partnerships Tab ─── */}
        {activeTab === 'partnerships' && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
                onClick={fetchCollaborations}
                className="h-9 px-3 rounded-lg border flex items-center gap-2 text-sm transition-colors duration-200"
                style={{ borderColor: 'rgba(255,255,255,0.1)', color: '#9ca3af', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
              <span className="ml-auto text-xs" style={{ color: '#9ca3af' }}>
                {collabTotal} {collabTotal === 1 ? 'inquiry' : 'inquiries'}
              </span>
            </div>

            {/* Partnership Stats */}
            {collabEntries.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {['brand_business', 'doctor_physician', 'influencer_kol'].map((type) => {
                  const info = COLLAB_TYPE_LABELS[type];
                  const count = collabEntries.filter((e) => e.collab_type === type).length;
                  const Icon = info.Icon;
                  return (
                    <div key={type} className="rounded-xl p-4 border" style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4" style={{ color: '#d6a84a' }} />
                        <p className="text-xs uppercase tracking-wider" style={{ color: '#9ca3af' }}>{info.label}</p>
                      </div>
                      <p className="text-2xl font-bold" style={{ color: '#d6a84a' }}>{count}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Partnerships Table */}
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
              <div className="overflow-x-auto">
                <table data-testid="admin-partnerships-table" className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      {['Type', 'Name', 'Email', 'Phone', 'Company', 'Message', 'Date'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#9ca3af' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {collabEntries.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: '#9ca3af' }}>No partnership inquiries yet</td></tr>
                    ) : collabEntries.map((entry, i) => {
                      const typeInfo = COLLAB_TYPE_LABELS[entry.collab_type] || { label: entry.collab_type, Icon: Handshake };
                      const TypeIcon = typeInfo.Icon;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <TypeIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#d6a84a' }} />
                              <span className="text-xs font-medium whitespace-nowrap" style={{ color: '#d6a84a' }}>{typeInfo.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-medium" style={{ color: '#f4f0e8' }}>{entry.full_name}</td>
                          <td className="px-4 py-3" style={{ color: '#c3bfb4' }}>{entry.email}</td>
                          <td className="px-4 py-3" style={{ color: '#c3bfb4' }}>{entry.phone}</td>
                          <td className="px-4 py-3 font-medium" style={{ color: '#f4f0e8' }}>{entry.company}</td>
                          <td className="px-4 py-3 max-w-[200px]">
                            <span className="text-xs truncate block" style={{ color: '#9ca3af' }}>{entry.message || '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: '#9ca3af' }}>
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div
    className="rounded-xl p-5 border"
    style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}
  >
    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#9ca3af' }}>{label}</p>
    <p className="text-2xl font-bold" style={{ color: '#d6a84a' }}>{value}</p>
  </div>
);

const LoginForm = ({ onLogin, error }) => {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [checking, setChecking] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !pass) return;
    setChecking(true);
    setLoginError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/stats`, {
        headers: { Authorization: 'Basic ' + btoa(user + ':' + pass) },
      });
      if (res.status === 401) {
        setLoginError('Invalid username or password');
      } else if (res.ok) {
        onLogin(user, pass);
      } else {
        setLoginError('Something went wrong. Please try again.');
      }
    } catch {
      setLoginError('Network error. Please check your connection.');
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#071525' }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-xl font-bold" style={{ color: '#f4f0e8', fontFamily: 'Inter, sans-serif' }}>VERSE Admin</h1>
          <p className="text-sm mt-2" style={{ color: '#9ca3af', fontFamily: 'Inter, sans-serif' }}>Sign in to manage waitlist</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {(loginError || error) && (
            <div className="p-3 rounded-xl text-sm text-center" style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}>
              {loginError || error}
            </div>
          )}
          <input
            data-testid="admin-username"
            type="text"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="Username"
            className="w-full h-12 px-4 rounded-xl border outline-none text-sm"
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderColor: 'rgba(255,255,255,0.1)',
              color: '#f4f0e8',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
            }}
          />
          <input
            data-testid="admin-password"
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="Password"
            className="w-full h-12 px-4 rounded-xl border outline-none text-sm"
            style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderColor: 'rgba(255,255,255,0.1)',
              color: '#f4f0e8',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
            }}
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={checking}
            className="w-full h-12 rounded-xl font-medium transition-colors duration-200 active:scale-[0.98] disabled:opacity-70"
            style={{ backgroundColor: '#d6a84a', color: '#071525', fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
          >
            {checking ? 'Verifying...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};
